import React, { useState, useEffect } from 'react';
import { Schedule, Enrollment, DiscountUsage } from '../../types';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { usePageTitle } from '../../hooks/usePageTitle';
import { formatDate, parseLocalDate } from '../../utils/dateUtils';

interface SessionWithCourse extends Schedule {
  courseTitle: string;
}

type LabGuideSaveState = 'idle' | 'saving' | 'saved' | 'error';
type RecordingSaveState = 'idle' | 'saving' | 'saved' | 'error';
type CompleteEnrollmentState = 'idle' | 'sending' | 'sent' | 'error';
type AbsentEnrollmentState = 'idle' | 'sending' | 'error';

const InstructorDashboard: React.FC = () => {
  usePageTitle('Instructor Dashboard');
  const [sessions, setSessions] = useState<SessionWithCourse[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [discountUsage, setDiscountUsage] = useState<DiscountUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [labGuideInputs, setLabGuideInputs] = useState<Record<string, string>>({});
  const [labGuideSaveState, setLabGuideSaveState] = useState<Record<string, LabGuideSaveState>>({});
  const [labGuideSaveError, setLabGuideSaveError] = useState<Record<string, string>>({});
  const [recordingInputs, setRecordingInputs] = useState<Record<string, string>>({});
  const [recordingSaveState, setRecordingSaveState] = useState<Record<string, RecordingSaveState>>({});
  const [recordingSaveError, setRecordingSaveError] = useState<Record<string, string>>({});
  const [completeState, setCompleteState] = useState<Record<string, CompleteEnrollmentState>>({});
  const [completeError, setCompleteError] = useState<Record<string, string>>({});
  const [absentState, setAbsentState] = useState<Record<string, AbsentEnrollmentState>>({});
  const [absentError, setAbsentError] = useState<Record<string, string>>({});
  const authFetch = useAuthFetch();

  const handleSendCertificate = async (enrollmentId: string, studentName: string, courseTitle: string) => {
    const confirmed = window.confirm(
      `Send the Course Completion Certificate to ${studentName} for "${courseTitle}"? This will mark the enrollment as completed and email the student their certificate.`
    );
    if (!confirmed) return;

    setCompleteState((prev) => ({ ...prev, [enrollmentId]: 'sending' }));
    setCompleteError((prev) => {
      const { [enrollmentId]: _omit, ...rest } = prev;
      return rest;
    });

    try {
      const res = await authFetch(`/api/instructors/me/enrollments/${enrollmentId}/complete`, {
        method: 'POST',
      });

      if (!res.ok) {
        let message = 'Failed to send completion certificate.';
        try {
          const body = await res.json();
          if (body && typeof body.message === 'string') message = body.message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }

      setCompleteState((prev) => ({ ...prev, [enrollmentId]: 'sent' }));
    } catch (err) {
      setCompleteState((prev) => ({ ...prev, [enrollmentId]: 'error' }));
      setCompleteError((prev) => ({
        ...prev,
        [enrollmentId]: err instanceof Error ? err.message : 'Failed to send completion certificate.',
      }));
    }
  };

  const handleLabGuideChange = (scheduleId: string, value: string) => {
    setLabGuideInputs((prev) => ({ ...prev, [scheduleId]: value }));
    setLabGuideSaveState((prev) => ({ ...prev, [scheduleId]: 'idle' }));
    setLabGuideSaveError((prev) => {
      const { [scheduleId]: _omit, ...rest } = prev;
      return rest;
    });
  };

  const handleLabGuideSave = async (scheduleId: string) => {
    const raw = (labGuideInputs[scheduleId] ?? '').trim();
    const payload = raw === '' ? null : raw;

    setLabGuideSaveState((prev) => ({ ...prev, [scheduleId]: 'saving' }));
    setLabGuideSaveError((prev) => {
      const { [scheduleId]: _omit, ...rest } = prev;
      return rest;
    });

    try {
      const res = await authFetch(`/api/instructors/me/sessions/${scheduleId}/lab-guide`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ labGuideUrl: payload }),
      });

      if (!res.ok) {
        let message = 'Failed to save lab guide link.';
        try {
          const body = await res.json();
          if (body && typeof body.message === 'string') message = body.message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }

      setSessions((prev) =>
        prev.map((s) => (s.id === scheduleId ? { ...s, labGuideUrl: payload } : s))
      );
      setLabGuideSaveState((prev) => ({ ...prev, [scheduleId]: 'saved' }));
    } catch (err) {
      setLabGuideSaveState((prev) => ({ ...prev, [scheduleId]: 'error' }));
      setLabGuideSaveError((prev) => ({
        ...prev,
        [scheduleId]: err instanceof Error ? err.message : 'Failed to save lab guide link.',
      }));
    }
  };

  const handleRecordingChange = (scheduleId: string, value: string) => {
    setRecordingInputs((prev) => ({ ...prev, [scheduleId]: value }));
    setRecordingSaveState((prev) => ({ ...prev, [scheduleId]: 'idle' }));
    setRecordingSaveError((prev) => {
      const { [scheduleId]: _omit, ...rest } = prev;
      return rest;
    });
  };

  const handleRecordingSave = async (scheduleId: string) => {
    const raw = (recordingInputs[scheduleId] ?? '').trim();
    const payload = raw === '' ? null : raw;

    setRecordingSaveState((prev) => ({ ...prev, [scheduleId]: 'saving' }));
    setRecordingSaveError((prev) => {
      const { [scheduleId]: _omit, ...rest } = prev;
      return rest;
    });

    try {
      const res = await authFetch(`/api/instructors/me/sessions/${scheduleId}/recording`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recordingUrl: payload }),
      });

      if (!res.ok) {
        let message = 'Failed to save recording link.';
        try {
          const body = await res.json();
          if (body && typeof body.message === 'string') message = body.message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }

      setSessions((prev) =>
        prev.map((s) => (s.id === scheduleId ? { ...s, recordingUrl: payload } : s))
      );
      setRecordingSaveState((prev) => ({ ...prev, [scheduleId]: 'saved' }));
    } catch (err) {
      setRecordingSaveState((prev) => ({ ...prev, [scheduleId]: 'error' }));
      setRecordingSaveError((prev) => ({
        ...prev,
        [scheduleId]: err instanceof Error ? err.message : 'Failed to save recording link.',
      }));
    }
  };

  const handleMarkAbsent = async (
    enrollmentId: string,
    studentName: string,
    courseTitle: string
  ) => {
    const confirmed = window.confirm(
      `Mark ${studentName} absent for "${courseTitle}"? They will receive an email with the session recording link.`
    );
    if (!confirmed) return;

    setAbsentState((prev) => ({ ...prev, [enrollmentId]: 'sending' }));
    setAbsentError((prev) => {
      const { [enrollmentId]: _omit, ...rest } = prev;
      return rest;
    });

    try {
      const res = await authFetch(`/api/instructors/me/enrollments/${enrollmentId}/absent`, {
        method: 'POST',
      });

      if (!res.ok) {
        let message = 'Failed to mark student absent.';
        try {
          const body = await res.json();
          if (body && typeof body.message === 'string') message = body.message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }

      setEnrollments((prev) =>
        prev.map((e) => (e.id === enrollmentId ? { ...e, status: 'absent' } : e))
      );
      setAbsentState((prev) => ({ ...prev, [enrollmentId]: 'idle' }));
    } catch (err) {
      setAbsentState((prev) => ({ ...prev, [enrollmentId]: 'error' }));
      setAbsentError((prev) => ({
        ...prev,
        [enrollmentId]: err instanceof Error ? err.message : 'Failed to mark student absent.',
      }));
    }
  };

  const handleUnmarkAbsent = async (enrollmentId: string, studentName: string) => {
    const confirmed = window.confirm(
      `Revert ${studentName} back to active enrollment? They will no longer be marked absent.`
    );
    if (!confirmed) return;

    setAbsentState((prev) => ({ ...prev, [enrollmentId]: 'sending' }));
    setAbsentError((prev) => {
      const { [enrollmentId]: _omit, ...rest } = prev;
      return rest;
    });

    try {
      const res = await authFetch(`/api/instructors/me/enrollments/${enrollmentId}/unmark-absent`, {
        method: 'POST',
      });

      if (!res.ok) {
        let message = 'Failed to revert student to active.';
        try {
          const body = await res.json();
          if (body && typeof body.message === 'string') message = body.message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }

      setEnrollments((prev) =>
        prev.map((e) => (e.id === enrollmentId ? { ...e, status: 'active' } : e))
      );
      setAbsentState((prev) => ({ ...prev, [enrollmentId]: 'idle' }));
    } catch (err) {
      setAbsentState((prev) => ({ ...prev, [enrollmentId]: 'error' }));
      setAbsentError((prev) => ({
        ...prev,
        [enrollmentId]: err instanceof Error ? err.message : 'Failed to revert student to active.',
      }));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sessionsRes, enrollmentsRes, usageRes] = await Promise.all([
          authFetch('/api/instructors/me/sessions'),
          authFetch('/api/instructors/me/enrollments'),
          authFetch('/api/discounts/me/usage'),
        ]);

        if (!sessionsRes.ok) {
          throw new Error('Failed to fetch sessions');
        }
        if (!enrollmentsRes.ok) {
          throw new Error('Failed to fetch enrollments');
        }
        if (!usageRes.ok) {
          throw new Error('Failed to fetch discount usage');
        }

        const sessionsData: SessionWithCourse[] = await sessionsRes.json();
        const enrollmentsData: Enrollment[] = await enrollmentsRes.json();
        const usageData: DiscountUsage[] = await usageRes.json();

        const sortedSessions = sessionsData.sort(
          (a, b) => parseLocalDate(a.sessionDate).getTime() - parseLocalDate(b.sessionDate).getTime()
        );
        setSessions(sortedSessions);
        setLabGuideInputs(
          Object.fromEntries(sortedSessions.map((s) => [s.id, s.labGuideUrl ?? '']))
        );
        setRecordingInputs(
          Object.fromEntries(sortedSessions.map((s) => [s.id, s.recordingUrl ?? '']))
        );
        setEnrollments(enrollmentsData);
        setDiscountUsage(usageData);
      } catch (err) {
        console.error('Error fetching instructor data:', err);
        setError(err instanceof Error ? err.message : 'An error occurred loading the dashboard.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authFetch]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading instructor dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="instructor-dashboard">
        <div className="error-message">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const upcomingSessions = sessions.filter((s) => s.status === 'scheduled');

  const getSessionEnrollments = (scheduleId: string): Enrollment[] =>
    enrollments.filter(
      (e) => e.scheduleId === scheduleId && (e.status === 'active' || e.status === 'absent')
    );

  const totalCommissions = discountUsage.reduce((sum, u) => sum + u.commissionAmount, 0);
  const totalDiscounts = discountUsage.reduce((sum, u) => sum + u.discountAmount, 0);
  const totalLearnersInUpcomingSessions = upcomingSessions.reduce((sum, session) => {
    return sum + getSessionEnrollments(session.id).length;
  }, 0);

  return (
    <div className="instructor-dashboard-page">
      <section className="dashboard-header instructor-hero">
        <div className="section-container">
          <h1 className="page-title">Instructor Dashboard</h1>
          <p className="page-subtitle">Everything you need for your upcoming sessions and earnings.</p>
          <div className="instructor-metric-grid">
            <div className="instructor-metric-card">
              <span className="instructor-metric-label">Upcoming Sessions</span>
              <span className="instructor-metric-value">{upcomingSessions.length}</span>
            </div>
            <div className="instructor-metric-card">
              <span className="instructor-metric-label">Learners Enrolled</span>
              <span className="instructor-metric-value">{totalLearnersInUpcomingSessions}</span>
            </div>
            <div className="instructor-metric-card">
              <span className="instructor-metric-label">Commission Earned</span>
              <span className="instructor-metric-value">${totalCommissions.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="section-container">
          <div className="instructor-section-header">
            <h2 className="section-title">Upcoming Sessions</h2>
          </div>
          {upcomingSessions.length === 0 ? (
            <div className="empty-state instructor-empty-state">
              <p>No upcoming sessions assigned to you.</p>
            </div>
          ) : (
            <div className="instructor-sessions">
              {upcomingSessions.map((session) => {
                const sessionEnrollments = getSessionEnrollments(session.id);
                return (
                  <article key={session.id} className="instructor-session-card">
                    <div className="session-card-header">
                      <div>
                        <h3>{session.courseTitle || 'Course'}</h3>
                        <p className="session-date">
                          {formatDate(session.sessionDate, {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </p>
                        <p className="session-time">
                          {session.startTime} - {session.endTime}
                        </p>
                      </div>
                      <div className="session-card-actions">
                        <span className="session-enrollment-badge">
                          {session.currentEnrollment} / {session.maxSeats} enrolled
                        </span>
                        {session.webinarJoinUrl && (
                          <a
                            href={session.webinarJoinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary btn-sm"
                          >
                            Join Teams Webinar
                          </a>
                        )}
                      </div>
                    </div>

                    {sessionEnrollments.length > 0 ? (
                      <div className="session-roster">
                        <h4>Participant Roster</h4>
                        <div className="table-responsive">
                          <table className="roster-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sessionEnrollments.map((enrollment) => {
                                const certState = completeState[enrollment.id] ?? 'idle';
                                const absState = absentState[enrollment.id] ?? 'idle';
                                const recordingSaved = Boolean((session.recordingUrl ?? '').trim());
                                const isAbsent = enrollment.status === 'absent';
                                const isCompleted = enrollment.status === 'completed' || certState === 'sent';
                                return (
                                  <tr key={enrollment.id}>
                                    <td>{enrollment.studentName}</td>
                                    <td>{enrollment.studentEmail}</td>
                                    <td>
                                      {isCompleted ? (
                                        <span style={{ color: '#16a34a', fontWeight: 600, fontSize: '13px' }}>
                                          Certificate Sent ✓
                                        </span>
                                      ) : isAbsent ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                                          <span style={{ color: '#92400e', fontWeight: 600, fontSize: '13px' }}>
                                            Marked Absent
                                          </span>
                                          <button
                                            type="button"
                                            className="btn btn-outline btn-sm"
                                            disabled={absState === 'sending'}
                                            onClick={() => handleUnmarkAbsent(enrollment.id, enrollment.studentName)}
                                          >
                                            {absState === 'sending' ? 'Reverting…' : 'Unmark Absent'}
                                          </button>
                                          {absState === 'error' && (
                                            <div style={{ color: '#c41e3a', fontSize: '12px' }}>
                                              {absentError[enrollment.id] || 'Failed to revert.'}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-start' }}>
                                          <button
                                            type="button"
                                            className="btn btn-secondary btn-sm"
                                            disabled={certState === 'sending'}
                                            onClick={() =>
                                              handleSendCertificate(
                                                enrollment.id,
                                                enrollment.studentName,
                                                session.courseTitle || 'this course'
                                              )
                                            }
                                          >
                                            {certState === 'sending' ? 'Sending…' : 'Send Course Completion Certificate'}
                                          </button>
                                          <button
                                            type="button"
                                            className="btn btn-outline btn-sm"
                                            disabled={!recordingSaved || absState === 'sending'}
                                            title={
                                              recordingSaved
                                                ? 'Email the student a link to the recording.'
                                                : 'Save a Recording Link below to enable.'
                                            }
                                            onClick={() =>
                                              handleMarkAbsent(
                                                enrollment.id,
                                                enrollment.studentName,
                                                session.courseTitle || 'this course'
                                              )
                                            }
                                          >
                                            {absState === 'sending' ? 'Marking…' : 'Mark Student Absent'}
                                          </button>
                                          {!recordingSaved && (
                                            <div style={{ flexBasis: '100%', color: '#64748b', fontSize: '12px' }}>
                                              Save a Recording Link below to enable Mark Student Absent.
                                            </div>
                                          )}
                                          {certState === 'error' && (
                                            <div style={{ flexBasis: '100%', color: '#c41e3a', fontSize: '12px' }}>
                                              {completeError[enrollment.id] || 'Failed to send certificate.'}
                                            </div>
                                          )}
                                          {absState === 'error' && (
                                            <div style={{ flexBasis: '100%', color: '#c41e3a', fontSize: '12px' }}>
                                              {absentError[enrollment.id] || 'Failed to mark absent.'}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="session-roster session-roster-empty">No participants enrolled yet.</div>
                    )}

                    <div className="session-lab-guide" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                      <h4 style={{ margin: '0 0 8px 0' }}>Lab Guide Link</h4>
                      <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#64748b' }}>
                        Paste a OneDrive link to your lab setup guide. Students receive it in the pre-class email
                        sent 7 days before the session. Leave blank if this course does not include a lab.
                      </p>
                      {session.labGuideUrl && (
                        <p style={{ margin: '0 0 8px 0', fontSize: '13px' }}>
                          Current:{' '}
                          <a href={session.labGuideUrl} target="_blank" rel="noopener noreferrer">
                            {session.labGuideUrl}
                          </a>
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <input
                          type="url"
                          placeholder="https://1drv.ms/..."
                          value={labGuideInputs[session.id] ?? ''}
                          onChange={(e) => handleLabGuideChange(session.id, e.target.value)}
                          style={{ flex: '1 1 320px', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '14px' }}
                        />
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleLabGuideSave(session.id)}
                          disabled={labGuideSaveState[session.id] === 'saving'}
                        >
                          {labGuideSaveState[session.id] === 'saving' ? 'Saving…' : 'Save Lab Guide Link'}
                        </button>
                      </div>
                      {labGuideSaveState[session.id] === 'saved' && (
                        <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#16a34a' }}>Saved.</p>
                      )}
                      {labGuideSaveState[session.id] === 'error' && (
                        <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#c41e3a' }}>
                          {labGuideSaveError[session.id] || 'Failed to save lab guide link.'}
                        </p>
                      )}
                    </div>

                    <div className="session-recording-link" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                      <h4 style={{ margin: '0 0 8px 0' }}>Recording Link</h4>
                      <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#64748b' }}>
                        Paste the session recording link (Teams Stream, OneDrive, etc.) after the live class.
                        Required before you can mark a student absent. Students who attended see this link on their dashboard too.
                      </p>
                      {session.recordingUrl && (
                        <p style={{ margin: '0 0 8px 0', fontSize: '13px' }}>
                          Current:{' '}
                          <a href={session.recordingUrl} target="_blank" rel="noopener noreferrer">
                            {session.recordingUrl}
                          </a>
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <input
                          type="url"
                          placeholder="https://..."
                          value={recordingInputs[session.id] ?? ''}
                          onChange={(e) => handleRecordingChange(session.id, e.target.value)}
                          style={{ flex: '1 1 320px', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '14px' }}
                        />
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleRecordingSave(session.id)}
                          disabled={recordingSaveState[session.id] === 'saving'}
                        >
                          {recordingSaveState[session.id] === 'saving' ? 'Saving…' : 'Save Recording Link'}
                        </button>
                      </div>
                      {recordingSaveState[session.id] === 'saved' && (
                        <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#16a34a' }}>Saved.</p>
                      )}
                      {recordingSaveState[session.id] === 'error' && (
                        <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#c41e3a' }}>
                          {recordingSaveError[session.id] || 'Failed to save recording link.'}
                        </p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="section section-alt">
        <div className="section-container">
          <div className="instructor-section-header">
            <h2 className="section-title">Discount Code Usage &amp; Commission Earnings</h2>
          </div>
          <div className="commission-summary">
            <div className="commission-summary-item">
              <span className="commission-label">Total Uses</span>
              <span className="commission-value">{discountUsage.length}</span>
            </div>
            <div className="commission-summary-item">
              <span className="commission-label">Total Discounts Given</span>
              <span className="commission-value">${totalDiscounts.toFixed(2)}</span>
            </div>
            <div className="commission-summary-item">
              <span className="commission-label">Total Commission Earned</span>
              <span className="commission-value">${totalCommissions.toFixed(2)}</span>
            </div>
          </div>

          {discountUsage.length > 0 ? (
            <div className="table-responsive">
              <table className="roster-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Customer</th>
                    <th>Tier</th>
                    <th>Discount</th>
                    <th>Commission</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {discountUsage
                    .sort((a, b) => parseLocalDate(b.dateUsed).getTime() - parseLocalDate(a.dateUsed).getTime())
                    .map((usage) => (
                      <tr key={usage.id}>
                        <td>{usage.discountCode}</td>
                        <td>{usage.customerEmail}</td>
                        <td>{usage.subscriptionTier}</td>
                        <td>${usage.discountAmount.toFixed(2)}</td>
                        <td>${usage.commissionAmount.toFixed(2)}</td>
                        <td>{formatDate(usage.dateUsed)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state instructor-empty-state">
              <p>No discount code usage recorded yet.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default InstructorDashboard;
